import isEmpty from "lodash/isEmpty";
import type { UseFormReturn } from "react-hook-form";
import type { Changes } from "creso-core/types";
import type { AccountCenterFormValues, SectionName } from "./form-schema";

export const SECTION_NAME_TO_CHANGE_SET_KEY_MAP: Record<
  SectionName,
  keyof Changes
> = {
  profile: "INVESTOR_PROFILE",
  account: "INVESTMENT_ACCOUNT",
  bank: "INVESTOR_BANK_ACCOUNT",
  address: "INVESTOR_ADDRESS_INFORMATION",
  contact: "INVESTOR_CONTACT_INFORMATION",
  nominees: "NOMINEE",
  other: "INVESTOR_PROFILE",
};

/**
 * Turns the account form's dirty fields into a change set payload.
 *
 * Only touched fields are sent, so an untouched section contributes nothing.
 * The bank and nominee arrays are all-or-nothing: if anything in them changed
 * the whole array goes up, because the API replaces the collection rather than
 * patching individual rows.
 */
export function buildAccountChanges(
  form: UseFormReturn<AccountCenterFormValues>
): Changes {
  const { dirtyFields } = form.formState;
  const sectionNames = Object.keys(
    SECTION_NAME_TO_CHANGE_SET_KEY_MAP
  ) as SectionName[];

  let changes: Changes = {};

  sectionNames.forEach((sectionName) => {
    const formData = form.getValues(sectionName);

    if (sectionName === "bank") {
      if (isEmpty(dirtyFields.bank)) return;

      const bankUpdates = form.getValues<"bank">("bank").reduce(
        (acc, b) => {
          acc.push({
            account_number: b.account_number,
            ifsc_code: b.ifsc_code,
            primary_account_holder_name: b.primary_account_holder_name,
            type: b.type,
            ...(typeof b.bank_account_id === "string"
              ? {}
              : { bank_account_id: b.bank_account_id }),
          });
          return acc;
        },
        [] as Array<Record<string, unknown>>
      );

      changes = {
        ...changes,
        [SECTION_NAME_TO_CHANGE_SET_KEY_MAP.bank]: bankUpdates,
      };
      return;
    }

    if (sectionName === "nominees") {
      if (isEmpty(dirtyFields.nominees)) return;

      const nomineeUpdates = form.getValues<"nominees">("nominees").reduce(
        (acc, n) => {
          acc.push({
            name: n.name,
            relationship: n.relationship,
            identity_type: n.identity_type,
            identity_number: n.identity_number,
            email: n.email,
            mobile: n.mobile,
            date_of_birth: n.date_of_birth,
            guardian_name: n.guardian_name,
            guardian_pan: n.guardian_pan,
            address_line_1: n.address_line_1,
            address_line_2: n.address_line_2,
            address_line_3: n.address_line_3,
            country: n.country,
            city: n.city,
            pin_code: n.pin_code,
            applicable_percentage: n.applicable_percentage,
            ...(typeof n.nominee_id === "string" && n.nominee_id
              ? { nominee_id: n.nominee_id }
              : {}),
          });
          return acc;
        },
        [] as Array<Record<string, unknown>>
      );

      changes = {
        ...changes,
        [SECTION_NAME_TO_CHANGE_SET_KEY_MAP.nominees]: nomineeUpdates,
      };
      return;
    }

    const updatedFields = Object.entries(dirtyFields[sectionName] || {})
      .filter(([, isDirty]) => isDirty)
      .map(([fieldName]) => fieldName);

    if (updatedFields.length === 0) return;

    const updateData = updatedFields.reduce(
      (acc, fieldName) => ({
        ...acc,
        [fieldName]: formData?.[fieldName as keyof typeof formData],
      }),
      {} as Record<string, unknown>
    );

    const changeSetKey = SECTION_NAME_TO_CHANGE_SET_KEY_MAP[sectionName];
    changes = {
      ...changes,
      [changeSetKey]: {
        ...changes[changeSetKey],
        ...updateData,
      },
    };
  });

  return changes;
}
